import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// TypeScript interfaces for API responses
export interface RestaurantOrderItem {
  orderId: string;
  orderNumber: string;
  customerName: string;
  riderName: string;
  totalAmount: number;
  status:
    | "pending"
    | "delivered"
    | "cancelled"
    | "in-progress"
    | "ready"
    | "preparing";
  eta: string;
  orderDate: string;
  action: "prepare" | "view" | "ready";
  isSupportOpen?: boolean;
}

export interface OrderItemDetail {
  id: string;
  name: string;
  image: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  mealDetails: {
    id: string;
    name: string;
    description: string;
    image: string;
  };
}

export interface CustomerDetails {
  id: string;
  name: string;
}

export interface RestaurantDetails {
  id: string;
  name: string;
  manager: string;
}

export interface OrderInfo {
  id: string;
  orderNumber: string;
  status: string;
  orderDate: string;
  estimatedTime: string;
  totalAmount: number;
  riderId: string;
  isSupportOpen?: boolean;
}

export interface RestaurantOrderDetails {
  orderInfo: OrderInfo;
  customerDetails: CustomerDetails;
  restaurantDetails: RestaurantDetails[];
  orderItems: OrderItemDetail[];
}

// API Response interfaces
interface RestaurantOrdersResponse {
  success: boolean;
  data: {
    orders: RestaurantOrderItem[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
  };
}

interface RestaurantOrderDetailsResponse {
  success: boolean;
  data: RestaurantOrderDetails;
}

// Redux state interface
interface RestaurantOrdersState {
  orders: RestaurantOrderItem[];
  currentOrderDetails: RestaurantOrderDetails | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  status: "idle" | "loading" | "success" | "failed";
  detailsStatus: "idle" | "loading" | "success" | "failed";
  error: string | null;
}

const initialState: RestaurantOrdersState = {
  orders: [],
  currentOrderDetails: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  status: "idle",
  detailsStatus: "idle",
  error: null,
};

// Async thunks for API calls
export const getAllRestaurantOrders = createAsyncThunk<
  RestaurantOrdersResponse,
  { page?: number; limit?: number; status?: string },
  { rejectValue: string }
>(
  "restaurantOrders/getAllRestaurantOrders",
  async (params, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_RESTAURANT_URL}/orders${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get<RestaurantOrdersResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant orders"
      );
    }
  }
);

export const getRestaurantOrderDetails = createAsyncThunk<
  RestaurantOrderDetailsResponse,
  string,
  { rejectValue: string }
>(
  "restaurantOrders/getRestaurantOrderDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<RestaurantOrderDetailsResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/orders/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order details"
      );
    }
  }
);

// Redux slice
const restaurantOrdersSlice = createSlice({
  name: "restaurantOrders",
  initialState,
  reducers: {
    clearCurrentOrderDetails: (state) => {
      state.currentOrderDetails = null;
      state.detailsStatus = "idle";
    },
    clearErrors: (state) => {
      state.error = null;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all restaurant orders
      .addCase(getAllRestaurantOrders.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllRestaurantOrders.fulfilled, (state, action) => {
        state.status = "success";
        state.orders = action.payload.data.orders;
        state.totalCount = action.payload.data.totalCount;
        state.currentPage = action.payload.data.currentPage;
        state.totalPages = action.payload.data.totalPages;
        state.error = null;
      })
      .addCase(getAllRestaurantOrders.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch restaurant orders";
      })

      // Get restaurant order details
      .addCase(getRestaurantOrderDetails.pending, (state) => {
        state.detailsStatus = "loading";
        state.error = null;
      })
      .addCase(getRestaurantOrderDetails.fulfilled, (state, action) => {
        state.detailsStatus = "success";
        state.currentOrderDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getRestaurantOrderDetails.rejected, (state, action) => {
        state.detailsStatus = "failed";
        state.error = action.payload || "Failed to fetch order details";
      });
  },
});

export const { clearCurrentOrderDetails, clearErrors, setCurrentPage } =
  restaurantOrdersSlice.actions;
export default restaurantOrdersSlice.reducer;
